import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Smartphone,
  Code2,
  Layers,
  Rocket,
  ShieldCheck,
  Cpu,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import FAQSection from "../components/homepage/FAQSection";
import PageHero, {
  SectionHeader,
  FeatureGrid,
  NumberedStrip,
} from "../components/shared/PageHero";

const SERVICES = [
  { icon: Smartphone, title: "iOS Development", text: "Native Swift & SwiftUI apps engineered for the Apple ecosystem — iPhone, iPad, watchOS, visionOS ready." },
  { icon: Code2, title: "Android Development", text: "Kotlin + Jetpack Compose. Built for Play Store, Samsung, and the entire Android long-tail." },
  { icon: Layers, title: "Flutter & React Native", text: "One codebase, two stores. Pixel-perfect cross-platform apps that still feel native." },
  { icon: Rocket, title: "MVP in 6 Weeks", text: "Pre-PMF? We ship a polished MVP in 6 weeks — store-ready with day-one ASO baked in." },
  { icon: ShieldCheck, title: "Scale & Maintenance", text: "Performance, security, release engineering and on-call. We run the app after we ship it." },
  { icon: Cpu, title: "Backend & APIs", text: "Node, Python, Go, Firebase. Real-time, offline-first, and LLM-integrated architectures." },
];

const PROCESS = [
  { title: "Discovery", text: "Product workshop, user journeys, ASO-aware feature scoping." },
  { title: "UI/UX Design", text: "Figma system, clickable prototype, usability tested." },
  { title: "Build", text: "Two-week sprints, demos every Friday, production code from day one." },
  { title: "QA & Store Launch", text: "Device-matrix QA, store listing, metadata + screenshots." },
  { title: "Scale", text: "Monitoring, crash-free 99.9%, iterative growth release cadence." },
];

const STACK = [
  "Swift · SwiftUI",
  "Kotlin · Jetpack Compose",
  "Flutter",
  "React Native",
  "Node.js · Express",
  "Python · FastAPI",
  "Go",
  "Firebase",
  "MongoDB · Postgres",
  "AWS · GCP",
  "Stripe · RevenueCat",
  "Sentry · Datadog",
];

const PORTFOLIO = [
  { category: "FinTech", name: "PaySprint", metric: "Launched in 12 weeks", market: "UAE" },
  { category: "Gaming", name: "LoopQuest", metric: "500K downloads in Y1", market: "USA" },
  { category: "Health", name: "CalmRoot", metric: "4.9★ on iOS & Android", market: "Global" },
  { category: "EdTech", name: "EduPond Kids", metric: "#3 in Education, IN", market: "India" },
];

const PRICING = [
  {
    tier: "MVP Sprint",
    price: "$14,900",
    cadence: "one-time",
    features: ["6-week MVP", "iOS or Android", "Up to 8 screens", "1 backend + admin", "Store launch"],
    highlighted: false,
  },
  {
    tier: "Full Product",
    price: "$38,000",
    cadence: "starting at",
    features: [
      "iOS + Android native",
      "Figma design system",
      "Backend + APIs",
      "Analytics + A/B stack",
      "ASO baked in",
      "3 months post-launch support",
    ],
    highlighted: true,
  },
  {
    tier: "Growth Partner",
    price: "Custom",
    cadence: "",
    features: [
      "Dedicated product pod",
      "Ongoing dev + ASO",
      "Multi-market release ops",
      "Weekly roadmap",
      "24/7 support",
    ],
    highlighted: false,
  },
];

export default function AppDevelopmentPage() {
  return (
    <>
      <Helmet>
        <title>
          App Development Company India — iOS, Android, Flutter | Lumotis
        </title>
        <meta
          name="description"
          content="Lumotis is a top app development company in India. iOS, Android, Flutter and React Native app development with ASO baked in from day one. MVP in 6 weeks."
        />
        <link rel="canonical" href="https://lumotis.com/app-development" />
      </Helmet>
      <Navbar />
      <main data-testid="appdev-page">
        <PageHero
          testid="appdev-hero"
          eyebrow="App Development"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "App Development" }]}
          title="The"
          titleAccent="App Development Company India"
          subtitle="turns to when an app needs to be beautifully built and built to rank. We design, engineer and launch iOS, Android, Flutter and React Native apps with ASO baked in from day one."
          primaryCta={{
            to: "/contact",
            label: "Start a project",
            testid: "appdev-cta-primary",
          }}
          secondaryCta={{
            to: "/#case-studies",
            label: "See portfolio",
            testid: "appdev-cta-secondary",
          }}
          rightSlot={
            <div className="bg-charcoal text-white p-8 shadow-2xl shadow-electric/20 rounded-2xl">
              <p className="text-overline text-electric mb-4">Active Projects</p>
              <ul className="space-y-4">
                {PORTFOLIO.map((p, i) => (
                  <li
                    key={p.name}
                    className="flex items-center justify-between border-b border-white/10 pb-3 last:border-0"
                  >
                    <div>
                      <div className="font-display font-bold">{p.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {p.category} · {p.market}
                      </div>
                    </div>
                    <span className="text-xs text-electric font-semibold">
                      {p.metric}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          }
        />

        {/* Services */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Capabilities"
              title="Full-stack app development, under one engineering roof."
              description="Six disciplines, one team. No handoffs between design, iOS, Android and backend."
            />
            <FeatureGrid items={SERVICES} testidPrefix="appdev-service" />
          </div>
        </section>

        {/* Process */}
        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mb-14">
              <p className="text-overline text-electric mb-3">02 — Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                Predictable sprints. Pixel-perfect releases.
              </h2>
            </div>
            <NumberedStrip steps={PROCESS} testidPrefix="appdev-step" />
          </div>
        </section>

        {/* Tech stack */}
        <section className="py-20 md:py-28 bg-white border-b border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="03 — Stack"
              title="The modern tech stack, used with taste."
              description="We pick the right tool for the app — not the trendiest one."
            />
            <div className="flex flex-wrap gap-3">
              {STACK.map((s) => (
                <span
                  key={s}
                  className="px-4 py-2 border border-zinc-300 text-sm text-charcoal hover:bg-charcoal hover:text-white transition-colors"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200" id="pricing">
          <div className="container">
            <SectionHeader
              eyebrow="04 — Pricing"
              title="Transparent engagement models."
              description="Fixed-price MVP, full product builds, or ongoing growth partnership."
              align="center"
            />
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {PRICING.map((p, idx) => (
                <motion.article
                  key={p.tier}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.06 }}
                  className={`p-8 md:p-10 flex flex-col ${
                    p.highlighted
                      ? "bg-charcoal text-white border border-charcoal -translate-y-2 shadow-xl shadow-electric/20 rounded-2xl"
                      : "bg-white border border-zinc-200"
                  }`}
                  data-testid={`appdev-pricing-${p.tier.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p
                    className={`text-overline ${p.highlighted ? "text-electric" : "text-zinc-500"} mb-3`}
                  >
                    {p.tier}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-5xl font-black tracking-tight">
                      {p.price}
                    </span>
                    {p.cadence && (
                      <span
                        className={`text-sm ${p.highlighted ? "text-zinc-400" : "text-zinc-500"}`}
                      >
                        {p.cadence}
                      </span>
                    )}
                  </div>
                  <ul className="mt-8 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <CheckCircle2
                          className="w-4 h-4 mt-0.5 text-electric flex-shrink-0"
                          strokeWidth={2.5}
                        />
                        <span
                          className={
                            p.highlighted ? "text-zinc-200" : "text-zinc-700"
                          }
                        >
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold transition-all ${
                      p.highlighted
                        ? "bg-electric text-white hover:bg-electric-400"
                        : "bg-charcoal text-white hover:bg-electric"
                    }`}
                  >
                    Start project <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <CTABanner />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}
